import { MEDIA_RESOLUTION } from '../media.js';
/**
 * Whether a photo fits the format it is placed in (spec 40 §4.5a, hub feedback 165):
 * the aspect ratio (width / height) each format shows without cropping or bars, and the
 * smallest short side that still looks sharp. Verified 2026-10-28 against Meta's IG User
 * Media reference: feed images must lie within 4:5 and 1.91:1 (outside, the API refuses
 * them); stories and reels are recommended at 9:16 "to avoid cropping or blank space".
 * Facebook feed posts show tall photos too, so they only warn above 1.91:1 and below 9:16.
 * A mismatch warns, it never blocks: a template covers its photo slot, Meta letterboxes a story.
 */
const NINE_SIXTEEN = 9 / 16;
/** Slack around 9:16, so a 1080x1920 export a few pixels off still counts as 9:16. */
const STORY_SLACK = 0.04;
/** Rounding slack for the feed range (a 1080x1350 photo is exactly 0.8). */
const EPSILON = 0.005;
const STORY_RANGE = { min: NINE_SIXTEEN - STORY_SLACK, max: NINE_SIXTEEN + STORY_SLACK };
const IG_FEED_RANGE = { min: 4 / 5, max: 1.91 };
export const FORMAT_ASPECT_RANGE = {
    ig_feed: IG_FEED_RANGE,
    ig_carousel: IG_FEED_RANGE,
    fb_feed: { min: NINE_SIXTEEN - STORY_SLACK, max: 1.91 },
    ig_story: STORY_RANGE,
    fb_story: STORY_RANGE,
    ig_reel: STORY_RANGE,
    fb_reel: STORY_RANGE,
};
/** Below this short side (px, after the crop) a photo looks soft on a phone: module 30's `low` resolution class. */
export const MEDIA_FIT_MIN_SHORT_SIDE = MEDIA_RESOLUTION.ok;
/**
 * How a story frame fits its photo (spec 40 section 4.5a, ADR-042): absent or `null` = the photo as it
 * is; `contain` = the editor chose to place the whole photo on a blurred copy of itself in 9:16
 * (module 30's `story_fill` variant). Never chosen automatically (owner decision 2026-10-29).
 */
export const VARIANT_MEDIA_FITS = ['contain'];
/** Size of module 30's `story_fill` variant: Meta's recommended story size. */
export const STORY_FILL_SIZE = { width: 1080, height: 1920 };
/** The part of the photo that is used: the original, or its crop (fractions of the original). */
export function effectiveSize(size, crop) {
    if (!size.width || !size.height)
        return null;
    return crop ? { width: size.width * crop.width, height: size.height * crop.height } : { width: size.width, height: size.height };
}
/**
 * What does not fit; empty when it fits or when the size is unknown (an unknown size is never flagged).
 * A photo placed with `fit: 'contain'` has no aspect problem: the background fills the frame. It is
 * shown whole, so its resolution is judged on the original, not on a stored crop.
 */
export function mediaFitIssues(format, size, crop, fit) {
    const used = effectiveSize(size, fit === 'contain' ? null : crop);
    if (!used)
        return [];
    const issues = [];
    const ratio = used.width / used.height;
    const range = FORMAT_ASPECT_RANGE[format];
    if (fit !== 'contain' && (ratio < range.min - EPSILON || ratio > range.max + EPSILON))
        issues.push('aspect_mismatch');
    if (Math.min(used.width, used.height) < MEDIA_FIT_MIN_SHORT_SIDE)
        issues.push('low_resolution');
    return issues;
}
//# sourceMappingURL=media-fit.js.map